import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Lock, KeyRound, AlertCircle, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { User, Tenant } from "@shared/schema";

export default function ResetPasswordPage() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const { toast } = useToast();

  const params = new URLSearchParams(search);
  const token = params.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState<string>("");
  const [success, setSuccess] = useState(false);

  const { data: authData, isLoading: authLoading } = useQuery<{ user: User; tenant: Tenant }>({
    queryKey: ["/api/auth/user"],
  });

  const isTokenMode = !!token;
  const isAuthenticatedMode = !isTokenMode && !!authData?.user?.needsPasswordReset;

  useEffect(() => {
    if (!isTokenMode && authData?.user && !authData.user.needsPasswordReset) {
      setLocation("/");
    }
  }, [authData, isTokenMode, setLocation]);

  // Only show the "no token" error after auth has fully settled — never while loading.
  // This prevents a flash of the error when the user arrives here via the accept-invite
  // flow (session was just created by the server; the /api/auth/user query hasn't resolved yet).
  useEffect(() => {
    if (!authLoading && !token && !authData?.user) {
      setValidationError("No reset token found. Please use the link from your email.");
    }
    // Clear the error the moment we confirm the user is authenticated.
    if (authData?.user) {
      setValidationError("");
    }
  }, [authLoading, token, authData]);

  const tokenResetMutation = useMutation({
    mutationFn: (data: { token: string; newPassword: string }) =>
      apiRequest("POST", "/api/auth/reset-password", data),
    onSuccess: () => {
      setSuccess(true);
      toast({ title: "Password Reset", description: "Your password has been reset. You can now log in." });
      setTimeout(() => setLocation("/"), 3000);
    },
    onError: (error: any) => {
      const msg = error?.message || "Failed to reset password. The link may have expired.";
      setValidationError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiRequest("POST", "/api/auth/change-password", data),
    onSuccess: (data: any) => {
      setSuccess(true);
      toast({ title: "Password Changed", description: "Your password has been updated successfully." });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      if (data?.nextStep === "email-otp") {
        // Assistant forced-reset flow: an OTP has been emailed; proceed to 2FA page.
        setTimeout(() => setLocation("/two-factor"), 800);
      } else {
        setTimeout(() => setLocation("/"), 1000);
      }
    },
    onError: (error: any) => {
      const msg = error?.message || "Failed to change password. Please try again.";
      setValidationError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const isPending = tokenResetMutation.isPending || changePasswordMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    if (newPassword.length < 8) {
      setValidationError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }

    if (isTokenMode) {
      tokenResetMutation.mutate({ token, newPassword });
    } else if (isAuthenticatedMode) {
      changePasswordMutation.mutate({ currentPassword: "temp", newPassword });
    } else {
      setValidationError("No reset token found. Please use the link from your email.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <CardTitle className="text-2xl">Reset Your Password</CardTitle>
          <CardDescription>
            {isAuthenticatedMode
              ? "You must set a new password before continuing."
              : "Enter a new password for your account."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
              <p className="text-green-700 dark:text-green-300 font-medium">
                Your password has been reset successfully.
              </p>
              <p className="text-sm text-muted-foreground">Redirecting you…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {isAuthenticatedMode && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
                  For security reasons, you need to set a new password before continuing.
                </div>
              )}

              {validationError && (
                <Alert variant="destructive" data-testid="alert-validation-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{validationError}</AlertDescription>
                </Alert>
              )}

              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  data-testid="input-new-password"
                />
              </div>

              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your new password"
                  required
                  data-testid="input-confirm-password"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isPending || (!isTokenMode && !isAuthenticatedMode)}
                data-testid="button-change-password"
              >
                <Lock className="w-4 h-4 mr-2" />
                {isPending ? "Saving…" : "Set New Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
